import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  Chip,
  Card,
  TextField,
  CircularProgress,
  IconButton,
  Drawer,
  MenuItem,
  Alert,
  Tooltip,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as InputsSVG } from "../asserts/logo/Inputs.svg";
import {
  setMetrics,
  setMetricsData,
  setApplicationSummary,
  setTrustWeights,
  setRisks,
  setUseCases,
  setLoading as setMetricsLoading,
  setError as setMetricsError,
} from "../store/slices/metricsSlice";
import LoadingOverlay from "../components/LoadingOverlay";
import ProjectRequired from "../components/ProjectRequired";
import { ReactComponent as DownloadSVG } from "../asserts/logo/download.svg";

// DOCX export libraries
import { saveAs } from "file-saver";
import {
  Document,
  Packer,
  Paragraph,
  Table as DocxTable,
  TableRow as DocxTableRow,
  TableCell as DocxTableCell,
  TextRun,
  WidthType,
  AlignmentType,
  BorderStyle,
  VerticalAlign,
  ImageRun,
} from "docx";

// Logo imports for DOCX export
import qualizealLogoPng from "../asserts/logo/QualizealLogo.png";
import validaiteLogoPng from "../asserts/logo/validaite_logo.png";

const baseUrl = process.env.REACT_APP_API_URL || "https://validaite.io/api";
const CATEGORIES = [
  "Accuracy",
  "Fairness",
  "Security",
  "Privacy",
  "Robustness",
  "Explainability",
  "Ethics",
];
const METRIC_TYPES = ["Quantitative", "Threshold", "Range", "Boolean"];
const EVIDENCE_LEVELS = ["Critical", "Medium", "Low"];
const STATUS_OPTIONS = ["Draft", "In Progress", "Final"];
const SIDEBAR_WIDTH = 0;

// Helper function to detect A7 VISION archetype
const isVisionArchetype = (archetypeCode) => {
  if (!archetypeCode || typeof archetypeCode !== 'string') return false;
  const code = archetypeCode.toUpperCase();
  return code.includes('A7') || code.includes('VISION');
};

// Helper function to detect A9/A12 AGENTIC archetype
const isAgenticArchetype = (archetypeCode) => {
  if (!archetypeCode || typeof archetypeCode !== 'string') return false;
  const code = archetypeCode.toUpperCase();
  return code.includes('A9') || code.includes('A12') || code.includes('AGENTIC') || code.includes('MULTI');
};

const MetricsDefinitionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  // const { projectId } = useParams();

  // Get data from location.state (navigation) or Redux (persisted)
  const locationData = location.state || {};

  // Redux state
  const metrics = useSelector((state) => state.metrics.metrics);
  const reduxApplicationSummary = useSelector(
    (state) => state.metrics.applicationSummary
  );
  const reduxTrustWeights = useSelector((state) => state.metrics.trustWeights);
  const reduxRisks = useSelector((state) => state.metrics.risks);
  const reduxUseCases = useSelector((state) => state.metrics.useCases);
  const loading = useSelector((state) => state.metrics.loading);
  const error = useSelector((state) => state.metrics.error);
  const projectId = useSelector((state) => state.project.currentProjectId);
  const userId = useSelector((state) => state.auth.userId);

  // Fallback to Redux if location.state is empty, or fetch from other Redux stores
  const applicationSummary =
    locationData.applicationSummary || reduxApplicationSummary || {};
  const trustWeights = locationData.trustWeights || reduxTrustWeights || {};
  const risks = locationData.risks || reduxRisks || [];
  const useCases = locationData.useCases || reduxUseCases || [];

  // Check if current archetype is A7 VISION or A9/A12 AGENTIC
  const isVision = isVisionArchetype(applicationSummary?.archetypeCode);
  const isAgentic = isAgenticArchetype(applicationSummary?.archetypeCode);

  // Local UI state (not persisted)
  const [searchQuery, setSearchQuery] = useState("");
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [newMetric, setNewMetric] = useState({
    riskName: "",
    name: "",
    type: "",
    threshold: "",
    evidence: "",
    status: "Draft",
  });

  // Fetch data from database on mount
  useEffect(() => {
    const fetchMetricsData = async () => {
      if (!projectId) {
        console.log("No projectId, skipping fetch");
        return;
      }

      try {
        dispatch(setMetricsLoading(true));
        const response = await axios.get(
          `${baseUrl}/metrics-definition?project_id=${projectId}`
        );

        if (response.data.success && response.data.data) {
          console.log("Fetched metrics data:", response.data.data);
          dispatch(setMetricsData(response.data.data));
        }
      } catch (err) {
        console.error("Error fetching metrics:", err);
        dispatch(setMetricsError("Failed to load data"));
      } finally {
        dispatch(setMetricsLoading(false));
      }
    };

    fetchMetricsData();
  }, [projectId, dispatch]);

  // Fetch risk identification data if not available in Redux
  useEffect(() => {
    const fetchRiskData = async () => {
      if (!projectId || risks.length > 0) return;

      try {
        const response = await axios.get(
          `${baseUrl}/risk-identification?project_id=${projectId}`
        );
        if (response.data.success && response.data.data) {
          const {
            risks: fetchedRisks,
            trustWeights: fetchedWeights,
            applicationSummary: fetchedSummary,
          } = response.data.data;

          if (fetchedRisks && fetchedRisks.length > 0) {
            dispatch(setRisks(fetchedRisks));
          }
          if (fetchedWeights && Object.keys(fetchedWeights).length > 0) {
            dispatch(setTrustWeights(fetchedWeights));
          }
          if (fetchedSummary && Object.keys(fetchedSummary).length > 0) {
            dispatch(setApplicationSummary(fetchedSummary));
          }
        }
      } catch (err) {
        console.error("Error fetching risk data:", err);
      }
    };

    fetchRiskData();
  }, [projectId, risks.length, dispatch]);

  /** === Backend call to Generate Metrics === */
  const handleGenerateMetrics = async () => {
    if (!projectId) {
      dispatch(
        setMetricsError("No project selected. Please select a project first.")
      );
      return;
    }

    dispatch(setMetricsLoading(true));
    dispatch(setMetricsError(null));

    try {
      const payload = {
        project_id: projectId,
        user_id: userId,
        generateMetrics: true,
        applicationSummary,
        trustWeights,
        risks,
        useCasesList: useCases,
      };
      console.log("🚀 Sending request payload:", payload);
      const res = await axios.post(`${baseUrl}/metrics-definition`, payload);
      console.log("Response from backend : ", res);
      if (res.data.success) {
        console.log("📩 Backend raw metrics:", res.data.data.metrics);
        dispatch(setMetrics(res.data.data.metrics || []));
      } else {
        dispatch(
          setMetricsError(res.data.error || "Failed to generate metrics.")
        );
      }
    } catch (err) {
      dispatch(setMetricsError(err.response?.data?.error || err.message));
    } finally {
      dispatch(setMetricsLoading(false));
    }
  };

  /** === Drawer Handlers === */
  const handleOpenDrawer = (metric = null, index = null) => {
    if (metric) {
      setNewMetric(metric);
      setEditIndex(index);
    } else {
      setNewMetric({
        riskName: "",
        name: "",
        type: "",
        threshold: "",
        evidence: "",
        status: "Draft",
      });
      setEditIndex(null);
    }
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => setOpenDrawer(false);

  /** === Save / Update Metric === */
  const handleSaveMetric = () => {
    let updated;
    if (editIndex !== null) {
      updated = [...metrics];
      updated[editIndex] = newMetric;
    } else {
      updated = [...metrics, newMetric];
    }
    dispatch(setMetrics(updated));
    setOpenDrawer(false);
  };

  const handleDeleteMetric = (index) => {
    const updated = metrics.filter((_, i) => i !== index);
    dispatch(setMetrics(updated));
  };

  // === File Download Handler ===
  const handleDownloadFile = async () => {
    try {
      const now = new Date();
      const timestamp = `${String(now.getDate()).padStart(2, "0")}_${String(now.getMonth() + 1).padStart(2, "0")}_${String(now.getFullYear()).slice(-2)}_${String(now.getHours()).padStart(2, "0")}_${String(now.getMinutes()).padStart(2, "0")}_${String(now.getSeconds()).padStart(2, "0")}`;

      // Fetch logo images and convert to ArrayBuffer
      const [qualizealResponse, validaiteResponse] = await Promise.all([
        fetch(qualizealLogoPng),
        fetch(validaiteLogoPng)
      ]);
      const qualizealBuffer = await qualizealResponse.arrayBuffer();
      const validaiteBuffer = await validaiteResponse.arrayBuffer();

      // Create table header row
      const headerRow = new DocxTableRow({
        children: [
          new DocxTableCell({
            children: [new Paragraph({ children: [new TextRun({ text: "S.No", bold: true, color: "FFFFFF" })] })],
            shading: { fill: "0094FB" },
          }),
          new DocxTableCell({
            children: [new Paragraph({ children: [new TextRun({ text: "Metric Name", bold: true, color: "FFFFFF" })] })],
            shading: { fill: "0094FB" },
          }),
          new DocxTableCell({
            children: [new Paragraph({ children: [new TextRun({ text: "Risk", bold: true, color: "FFFFFF" })] })],
            shading: { fill: "0094FB" },
          }),
          new DocxTableCell({
            children: [new Paragraph({ children: [new TextRun({ text: "Category", bold: true, color: "FFFFFF" })] })],
            shading: { fill: "0094FB" },
          }),
          new DocxTableCell({
            children: [new Paragraph({ children: [new TextRun({ text: "Type", bold: true, color: "FFFFFF" })] })],
            shading: { fill: "0094FB" },
          }),
          new DocxTableCell({
            children: [new Paragraph({ children: [new TextRun({ text: "Threshold", bold: true, color: "FFFFFF" })] })],
            shading: { fill: "0094FB" },
          }),
          new DocxTableCell({
            children: [new Paragraph({ children: [new TextRun({ text: "Status", bold: true, color: "FFFFFF" })] })],
            shading: { fill: "0094FB" },
          }),
        ],
      });

      // Create table body rows
      const bodyRows = metrics.map((m, index) =>
        new DocxTableRow({
          children: [
            new DocxTableCell({ children: [new Paragraph(String(index + 1))] }),
            new DocxTableCell({ children: [new Paragraph(String(m.metricName || m.name || ""))] }),
            new DocxTableCell({ children: [new Paragraph(String(m.riskName || ""))] }),
            new DocxTableCell({ children: [new Paragraph(String(m.riskCategory || ""))] }),
            new DocxTableCell({ children: [new Paragraph(String(m.metricType || ""))] }),
            new DocxTableCell({ children: [new Paragraph(String(m.threshold || ""))] }),
            new DocxTableCell({ children: [new Paragraph(String(m.status || ""))] }),
          ],
        })
      );

      // Create document
      const doc = new Document({
        sections: [
          {
            properties: {},
            children: [
              // Header with Dual Logos - Qualizeal (left) and ValidAIte (right)
              new DocxTable({
                width: { size: 100, type: WidthType.PERCENTAGE },
                borders: {
                  top: { style: BorderStyle.NONE },
                  bottom: { style: BorderStyle.NONE },
                  left: { style: BorderStyle.NONE },
                  right: { style: BorderStyle.NONE },
                  insideHorizontal: { style: BorderStyle.NONE },
                  insideVertical: { style: BorderStyle.NONE },
                },
                rows: [
                  new DocxTableRow({
                    children: [
                      // Left cell - Qualizeal Logo
                      new DocxTableCell({
                        children: [
                          new Paragraph({
                            children: [
                              new ImageRun({
                                data: qualizealBuffer,
                                transformation: {
                                  width: 120,
                                  height: 40,
                                },
                                type: "png",
                              }),
                            ],
                            alignment: AlignmentType.LEFT,
                          }),
                        ],
                        width: { size: 50, type: WidthType.PERCENTAGE },
                        verticalAlign: VerticalAlign.CENTER,
                      }),
                      // Right cell - ValidAIte Logo with tagline
                      new DocxTableCell({
                        children: [
                          new Paragraph({
                            children: [
                              new ImageRun({
                                data: validaiteBuffer,
                                transformation: {
                                  width: 120,
                                  height: 40,
                                },
                                type: "png",
                              }),
                            ],
                            alignment: AlignmentType.RIGHT,
                          }),
                          new Paragraph({
                            children: [
                              new TextRun({
                                text: "Trust First. Risk Aware. Enterprise Ready.",
                                italics: true,
                                size: 16,
                                color: "666666",
                              }),
                            ],
                            alignment: AlignmentType.RIGHT,
                            spacing: { before: 50 },
                          }),
                        ],
                        width: { size: 50, type: WidthType.PERCENTAGE },
                        verticalAlign: VerticalAlign.CENTER,
                      }),
                    ],
                  }),
                ],
              }),
              // Horizontal line
              new Paragraph({
                border: {
                  bottom: { color: "0094FB", space: 1, style: BorderStyle.SINGLE, size: 6 },
                },
                spacing: { after: 400 },
              }),
              // Report Title
              new Paragraph({
                children: [
                  new TextRun({
                    text: "Metrics Definition Report",
                    bold: true,
                    size: 36,
                    color: "1A1A1A",
                  }),
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 300 },
              }),
              // Report Info
              new Paragraph({
                children: [
                  new TextRun({ text: "Application: ", bold: true }),
                  new TextRun({ text: applicationSummary.applicationName || "N/A" }),
                ],
                spacing: { after: 100 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Archetype: ", bold: true }),
                  new TextRun({ text: applicationSummary.archetypeCode || "N/A" }),
                ],
                spacing: { after: 100 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Domain: ", bold: true }),
                  new TextRun({ text: applicationSummary.domain || "N/A" }),
                ],
                spacing: { after: 100 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Risk Tier: ", bold: true }),
                  new TextRun({ text: applicationSummary.riskTier || "N/A" }),
                ],
                spacing: { after: 100 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Generated: ", bold: true }),
                  new TextRun({ text: now.toLocaleString() }),
                ],
                spacing: { after: 300 },
              }),
              // Metrics Section
              new Paragraph({
                children: [
                  new TextRun({
                    text: `Defined Metrics (${metrics.length})`,
                    bold: true,
                    size: 28,
                    color: "0094FB",
                  }),
                ],
                spacing: { before: 200, after: 200 },
              }),
              // Metrics Table
              new DocxTable({
                width: { size: 100, type: WidthType.PERCENTAGE },
                rows: [headerRow, ...bodyRows],
              }),
              // Footer
              new Paragraph({
                children: [
                  new TextRun({
                    text: "\n\n© Qualizeal | ValidAIte - AI Governance & Risk Management Platform",
                    size: 18,
                    color: "888888",
                    italics: true,
                  }),
                ],
                alignment: AlignmentType.CENTER,
                spacing: { before: 400 },
              }),
            ],
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, `Metrics_Definition_Report_${timestamp}.docx`);
    } catch (err) {
      console.error("DOCX export failed:", err);
      alert("Failed to generate report. Please try again.");
    }
  };

  /** === UI Starts === */
  return (
    <Box
      sx={{
        flex: 1,
        bgcolor: "#f3f2f5ff",
        minHeight: "100vh",
        ml: { xs: 0, md: `${SIDEBAR_WIDTH}px` },
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {/* === Sticky Header === */}
      <Box
        sx={{
          position: "sticky",
          top: 50,
          zIndex: 100,
          backgroundColor: "#f3f2f5ff",
          py: 2.5,
          px: 2,
        }}
      >
        <Typography variant="h5" fontWeight={600} color="#111827">
          Define Metrics
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Evaluation Metric Definition & Assurance Criteria Design
        </Typography>
      </Box>

      <Box sx={{ p: 4, pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* === Card 1: Inputs from Previous Stage === */}
        <Card
          sx={{
            mb: 3,
            p: 3,
            borderRadius: 0,
            boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
            bgcolor: "#fff",
          }}
        >
          <Box display="flex" alignItems="center" mb={2}>
            <InputsSVG style={{ width: 22, height: 22, marginRight: 8 }} />
            <Typography variant="subtitle1" fontWeight={700}>
              Inputs from Previous Stages
            </Typography>
          </Box>


          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="#6b7280">
                Application
              </Typography>
              <Typography fontWeight={600}>
                {applicationSummary?.applicationName || "Not specified"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="#6b7280">
                Archetype Code
              </Typography>
              <Typography fontWeight={600}>
                {applicationSummary?.archetypeCode || "Not specified"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="#6b7280">
                Risk Tier
              </Typography>
              <Typography fontWeight={600} color="error">
                {applicationSummary?.riskTier || "Not specified"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="#6b7280">
                Risks Identified
              </Typography>
              <Typography fontWeight={600}>{risks?.length || 0}</Typography>
            </Grid>
          </Grid>

          <Box mt={2}>
            <Typography variant="body2" color="#6b7280">
              Risks
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
              {/* {risks?.slice(0, 4).map((r, idx) => ( */}
              {risks?.map((r, idx) => (
                <Chip
                  key={idx}
                  label={r.title}
                  color="info"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>

          <Box mt={3}>
<Button
  variant="contained"
  onClick={handleGenerateMetrics}
  disabled={loading}
  sx={{
    borderRadius: 2,
    textTransform: "none",
    px: 3,
    fontWeight: 700,
    bgcolor: "#3B82F6",
    color: "#FFFFFF",
    "&:hover": { bgcolor: "#2563EB" },
  }}
>
  {loading ? "Mapping Metrics..." : "Defining Metrics for Risks"}
</Button>

          </Box>
        </Card>

        {/* === Attributes Section === */}
        <Card
          sx={{
            mb: 3,
            p: 3,
            borderRadius: 0,
            boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
            bgcolor: "#fff",
          }}
        >
          <Typography fontWeight={700} mb={2}>
            Attributes/Metrics
          </Typography>
          <Grid container spacing={4}>
            {CATEGORIES.map((cat) => (
              <Grid item xs={6} sm={4} md={2} key={cat}>
                <Box>
                  <Typography variant="body2" color="#6b7280">
                    {cat}
                  </Typography>
                  <Typography fontWeight={700}>
                    {metrics.filter((m) => m.riskCategory === cat).length}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Card>

        {/* === Metrics Table === */}
        {/* === Metrics Table === */}
        <Card
          sx={{
            p: 3,
            borderRadius: 0,
            boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
            bgcolor: "#fff",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography fontWeight="bold">
              Trust Metrics Library ({metrics.length} Metrics)
            </Typography>

            <Box display="flex" alignItems="center" gap={2}>
              <TextField
                size="small"
                placeholder="Search"
                variant="outlined"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  bgcolor: "#f9fafb",
                  "& fieldset": { borderColor: "#E5E7EB" },
                }}
              />
              <Button
                startIcon={<Add />}
                sx={{
                  color: "#2563EB",
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "0.9rem",
                }}
                onClick={() => handleOpenDrawer()}
              >
                Add Metric
              </Button>
            </Box>
          </Box>

          {/* Filter metrics based on search query */}
          {(() => {
            const filteredMetrics = metrics.filter(
              (metric) =>
                metric.riskCategory
                  ?.toLowerCase()
                  .includes(searchQuery.toLowerCase()) ||
                metric.riskName
                  ?.toLowerCase()
                  .includes(searchQuery.toLowerCase()) ||
                metric.metricName
                  ?.toLowerCase()
                  .includes(searchQuery.toLowerCase()) ||
                metric.metricType
                  ?.toLowerCase()
                  .includes(searchQuery.toLowerCase()) ||
                metric.evidence
                  ?.toLowerCase()
                  .includes(searchQuery.toLowerCase()) ||
                metric.status?.toLowerCase().includes(searchQuery.toLowerCase())
            );

            return filteredMetrics.length === 0 ? (
              <Box textAlign="center" py={5}>
                <Typography fontWeight={600}>
                  {metrics.length === 0
                    ? "No metrics yet"
                    : "No matching metrics found"}
                </Typography>
                <Typography variant="body2" color="#6B7280">
                  {metrics.length === 0
                    ? "Click Generate Metrics to populate"
                    : "Try adjusting your search query."}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ maxHeight: 350, overflowY: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "0.9rem",
                    textAlign: "left",
                  }}
                >
                  <thead
                    style={{
                      background: "#fff",
                      position: "sticky",
                      top: 0,
                      zIndex: 3,
                    }}
                  >
                    <tr>
                      {[
                        "Attributes",
                        "Risk Name",
                        "Metric",
                        "Type",
                        "Threshold",
                        "Severity",
                        "Status",
                        "Actions",
                      ].map((h, i) => (
                        <th
                          key={i}
                          style={{
                            padding: "12px 10px",
                            fontWeight: 600,
                            fontSize: "0.85rem",
                            color: "#374151",
                            borderBottom: "1px solid #E5E7EB",
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {filteredMetrics.map((metric, index) => (
                      <tr
                        key={index}
                        style={{ borderBottom: "1px solid #E5E7EB" }}
                      >
                        <td
                          style={{
                            padding: "12px 10px",
                            color: "#2563EB",
                            fontWeight: 600,
                          }}
                        >
                          {metric.riskCategory}
                        </td>

                        <td
                          style={{
                            padding: "12px 10px",
                            color: "#2563EB",
                            fontWeight: 600,
                          }}
                        >
                          {metric.riskName}
                        </td>

                        <td
                          style={{
                            padding: "12px 10px",
                            cursor: "pointer",
                          }}
                        >
                          <Tooltip
                            title={
                              metric.description || "No description available"
                            }
                            arrow
                            placement="top"
                          >
                            <span>{metric.metricName}</span>
                          </Tooltip>
                        </td>

                        <td style={{ padding: "12px 10px" }}>
                          {metric.metricType || "Quantitative"}
                        </td>
                        <td>{metric.threshold || "--"}</td>

                        {/* <td style={{ padding: "12px 10px" }}>
                      ≥{metric.threshold?.target ?? "--"}
                    </td> */}

                        <td style={{ padding: "12px 10px" }}>
                          <Chip
                            label={metric.evidence}
                            size="small"
                            sx={{
                              bgcolor:
                                metric.evidence === "Critical"
                                  ? "#FEE2E2"
                                  : metric.evidence === "Medium"
                                  ? "#FEF3C7"
                                  : "#DCFCE7",
                              color:
                                metric.evidence === "Critical"
                                  ? "#B91C1C"
                                  : metric.evidence === "Medium"
                                  ? "#92400E"
                                  : "#166534",
                              fontWeight: 600,
                              fontSize: "0.75rem",
                            }}
                          />
                        </td>

                        <td style={{ padding: "12px 10px" }}>
                          {metric.status}
                        </td>

                        <td style={{ padding: "12px 10px" }}>
                          <IconButton
                            size="small"
                            sx={{ color: "#2563EB" }}
                            onClick={() => handleOpenDrawer(metric, index)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            sx={{ color: "#DC2626" }}
                            onClick={() => handleDeleteMetric(index)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            );
          })()}
        </Card>

        {/* === Drawer for Add/Edit Metric === */}
        <Drawer
          anchor="right"
          open={openDrawer}
          onClose={handleCloseDrawer}
          PaperProps={{
            sx: { width: 400, p: 3, borderRadius: "12px 0 0 12px" },
          }}
        >
          <Typography variant="h6" fontWeight={600} mb={2}>
            {editIndex !== null ? "Edit Metric" : "Add New Metric"}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Risk Name"
                fullWidth
                value={newMetric.riskName}
                onChange={(e) =>
                  setNewMetric({ ...newMetric, riskName: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Metric Name"
                fullWidth
                value={newMetric.name}
                onChange={(e) =>
                  setNewMetric({ ...newMetric, name: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Type"
                select
                fullWidth
                value={newMetric.type}
                onChange={(e) =>
                  setNewMetric({ ...newMetric, type: e.target.value })
                }
              >
                {METRIC_TYPES.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Threshold"
                fullWidth
                value={newMetric.threshold}
                onChange={(e) =>
                  setNewMetric({ ...newMetric, threshold: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Evidence"
                select
                fullWidth
                value={newMetric.evidence}
                onChange={(e) =>
                  setNewMetric({ ...newMetric, evidence: e.target.value })
                }
              >
                {EVIDENCE_LEVELS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Box display="flex" justifyContent="flex-end" mt={3} gap={1}>
            <Button variant="outlined" onClick={handleCloseDrawer}>
              Cancel
            </Button>
            <Button
              variant="contained"
              sx={{ bgcolor: "#2563EB", "&:hover": { bgcolor: "#1E40AF" } }}
              onClick={handleSaveMetric}
            >
              {editIndex !== null ? "Save Changes" : "Add Metric"}
            </Button>
          </Box>
        </Drawer>

        {/* === Footer Buttons === */}
        <Box display="flex" justifyContent="flex-end" mt={4} gap={2}>
          <Button
            variant="outlined"
            startIcon={<DownloadSVG style={{ width: 18, height: 18 }} />}
            onClick={handleDownloadFile}
            sx={{
              borderColor: "#3B82F6",
              color: "#3B82F6",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: 2,
              px: 3,
              py: 1,
              "&:hover": { bgcolor: "#EBF5FF", borderColor: "#2563EB" },
            }}
          >
            Download File
          </Button>

          <Button
            variant="contained"
            color="primary"
            disabled={metrics.length === 0}
            onClick={() => {
              if (isVision || isAgentic) {
                // A7 VISION or A9/A12 AGENTIC: Skip Test Suite, go directly to Test Lab
                navigate(`/project/${projectId}/test-evaluation`, {
                  state: {
                    metrics: metrics,
                    applicationSummary: applicationSummary,
                    trustWeights: trustWeights,
                    risks: risks,
                    useCases,
                  },
                });
              } else {
                // A3 RAG: Go to Test Suite (usecase-dataset-generation)
                navigate(`/project/${projectId}/usecase-dataset-generation`, {
                  state: {
                    metrics: metrics,
                    applicationSummary: applicationSummary,
                    trustWeights: trustWeights,
                    risks: risks,
                    useCases,
                  },
                });
              }
            }}
            sx={{
              "&.Mui-disabled": {
                bgcolor: "#D1D5DB",
                color: "#9CA3AF",
              },
            }}
          >
            {(isVision || isAgentic) ? "Go to Test Lab →" : "Go to Test Suite →"}
          </Button>
        </Box>

        {/* Loading Overlay */}
        <LoadingOverlay open={loading} message="Generating Metrics..." />
      </Box>
    </Box>
  );
};

export default MetricsDefinitionPage;

// import React, { useState } from "react";
// import {
//   Box,
//   Typography,
//   Grid,
//   Button,
//   Chip,
//   Card,
//   CircularProgress,
//   Alert,
//   LinearProgress,
// } from "@mui/material";
// import axios from "axios";
// import { useNavigate, useLocation } from "react-router-dom";

// const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
// const CATEGORIES = [
//   "Accuracy",
//   "Fairness",
//   "Privacy",
//   "Ethics",
//   "Robustness",
//   "Security",
//   "Reliability",
// ];
// const SIDEBAR_WIDTH = 0;

// const MetricsDefinitionPage = () => {
//   const navigate = useNavigate();
//   const location = useLocation();
//   const { applicationSummary, trustWeights, risks } = location.state || {};

//   const [metrics, setMetrics] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);

//   const handleGenerateMetrics = async () => {
//     setLoading(true);
//     setError(null);
//     try {
//       const payload = {
//         generateMetrics: true,
//         applicationSummary,
//         trustWeights,
//         risks,
//       };
//       const res = await axios.post(`${baseUrl}/metrics-definition`, payload);
//       if (res.data.success) {
//         setMetrics(res.data.data.metrics || []);
//       } else {
//         setError(res.data.error || "Failed to generate metrics.");
//       }
//     } catch (err) {
//       setError(err.response?.data?.error || err.message);
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <Box
//       sx={{
//         flex: 1,
//         p: 4,
//         bgcolor: "#f9fafb",
//         minHeight: "100vh",
//         ml: { xs: 0, md: `${SIDEBAR_WIDTH}px` },
//       }}
//     >
//       {/* Page Header */}
//       <Typography variant="h5" fontWeight="bold" mb={1}>
//         Trust Metrics Definition
//       </Typography>
//       <Typography variant="body2" color="text.secondary" mb={3}>
//         Transform risks into measurable, quantifiable trust metrics with thresholds.
//       </Typography>

//       {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

//       {/* Previous Stage Card */}
//       <Card sx={{ p: 3, mb: 3, borderRadius: 3 }}>
//         <Typography fontWeight="bold" gutterBottom>
//           Inputs from Previous Stage
//         </Typography>
//         <Grid container spacing={2}>
//           <Grid item xs={6} md={3}>
//             <Typography variant="body2" color="textSecondary">Application</Typography>
//             <Typography fontWeight="bold">{applicationSummary?.applicationName}</Typography>
//           </Grid>
//           <Grid item xs={6} md={3}>
//             <Typography variant="body2" color="textSecondary">Archetype Code</Typography>
//             <Typography fontWeight="bold">{applicationSummary?.archetypeCode}</Typography>
//           </Grid>
//           <Grid item xs={6} md={3}>
//             <Typography variant="body2" color="textSecondary">Risk Tier</Typography>
//             <Typography fontWeight="bold" color="error">{applicationSummary?.riskTier}</Typography>
//           </Grid>
//           <Grid item xs={6} md={3}>
//             <Typography variant="body2" color="textSecondary">Risks Identified</Typography>
//             <Typography fontWeight="bold">{risks?.length || 0}</Typography>
//           </Grid>
//         </Grid>

//         <Box mt={2}>
//           <Typography variant="body2" color="textSecondary">Modifiers</Typography>
//           <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
//             {risks?.slice(0, 4).map((r, idx) => (
//               <Chip key={idx} label={r.title} color="info" variant="outlined" />
//             ))}
//           </Box>
//         </Box>

//         <Box mt={2}>
//           <Button
//             variant="outlined"
//             onClick={handleGenerateMetrics}
//             disabled={loading}
//           >
//             {loading ? <CircularProgress size={20} /> : "Generate Metrics"}
//           </Button>
//         </Box>
//       </Card>

//       {/* Attributes Section */}
//       <Card sx={{ mb: 3, p: 2 }}>
//         <Typography fontWeight="bold" mb={2}>
//           Attributes
//         </Typography>
//         <Grid container spacing={2}>
//           {CATEGORIES.map((cat) => (
//             <Grid item xs={6} sm={3} md={1.7} key={cat}>
//               <Box textAlign="center">
//                 <Typography variant="body2" fontWeight="bold">{cat}</Typography>
//                 <Typography variant="h6" color="text.secondary">
//                   {trustWeights?.[cat] || 0}
//                 </Typography>
//               </Box>
//             </Grid>
//           ))}
//         </Grid>
//       </Card>

//       {/* Trust Metrics Library (Table Style) */}
//       <Card sx={{ p: 3 }}>
//         <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
//           <Typography fontWeight="bold">
//             Trust Metrics Library ({metrics.length} Metric{metrics.length !== 1 && "s"})
//           </Typography>

//           <Box display="flex" alignItems="center" gap={1}>
//             <input
//               type="text"
//               placeholder="Search"
//               style={{
//                 border: "1px solid #ccc",
//                 borderRadius: "20px",
//                 padding: "6px 12px",
//                 outline: "none",
//                 width: "160px",
//               }}
//             />
//             <Button
//               variant="text"
//               sx={{ color: "#1976d2", fontWeight: 500, textTransform: "none" }}
//             >
//               + Add Metric
//             </Button>
//           </Box>
//         </Box>

//         <Box sx={{ overflowX: "auto" }}>
//           <table
//             style={{
//               width: "100%",
//               borderCollapse: "collapse",
//               fontSize: "14px",
//               textAlign: "left",
//             }}
//           >
//             <thead>
//               <tr style={{ borderBottom: "1px solid #e0e0e0", background: "#fafafa" }}>
//                 {["Risk Name", "Metric", "Type", "Threshold", "Evidence", "Weight", "Status", "Actions"].map(
//                   (header, i) => (
//                     <th key={i} style={{ padding: "10px 8px", fontWeight: 600, color: "#333" }}>
//                       {header}
//                     </th>
//                   )
//                 )}
//               </tr>
//             </thead>

//             <tbody>
//               {metrics.length > 0 ? (
//                 metrics.map((m, i) => (
//                   <tr key={i} style={{ borderBottom: "1px solid #f0f0f0", backgroundColor: i % 2 ? "#fff" : "#fcfcfc" }}>
//                     <td style={{ padding: "10px 8px", color: "#1976d2", fontWeight: 500 }}>
//                       {m.riskName || "—"}
//                     </td>
//                     <td style={{ padding: "10px 8px" }}>{m.name}</td>
//                     <td style={{ padding: "10px 8px" }}>{m.metricType}</td>
//                     <td style={{ padding: "10px 8px" }}>
//                       {m.threshold?.target ? `≥${m.threshold.target}` : "—"}
//                     </td>

//                     {/* Evidence chip */}
//                     <td style={{ padding: "10px 8px" }}>
//                       <Chip
//                         label={m.evidence || "Medium"}
//                         size="small"
//                         sx={{
//                           backgroundColor:
//                             m.evidence === "Critical"
//                               ? "#ffebee"
//                               : m.evidence === "Medium"
//                               ? "#fff8e1"
//                               : "#e8f5e9",
//                           color:
//                             m.evidence === "Critical"
//                               ? "#d32f2f"
//                               : m.evidence === "Medium"
//                               ? "#f57c00"
//                               : "#388e3c",
//                           fontWeight: 500,
//                         }}
//                       />
//                     </td>

//                     {/* Weight bar */}
//                     <td style={{ padding: "10px 8px", minWidth: "120px" }}>
//                       <Box display="flex" alignItems="center" gap={1}>
//                         <LinearProgress
//                           variant="determinate"
//                           value={13}
//                           sx={{
//                             flex: 1,
//                             height: 6,
//                             borderRadius: 4,
//                             backgroundColor: "#e0e0e0",
//                           }}
//                         />
//                         <Typography variant="caption" sx={{ minWidth: 30 }}>
//                           13%
//                         </Typography>
//                       </Box>
//                     </td>

//                     {/* Status */}
//                     <td style={{ padding: "10px 8px" }}>
//                       <Chip
//                         label={m.status || "Draft"}
//                         size="small"
//                         variant="outlined"
//                       />
//                     </td>

//                     {/* Actions */}
//                     <td style={{ padding: "10px 8px" }}>
//                       <Button
//                         size="small"
//                         sx={{
//                           color: "#1976d2",
//                           minWidth: 0,
//                           fontSize: 14,
//                           p: 0.5,
//                         }}
//                       >
//                         ✏️
//                       </Button>
//                       <Button
//                         size="small"
//                         sx={{
//                           color: "#d32f2f",
//                           minWidth: 0,
//                           fontSize: 14,
//                           p: 0.5,
//                         }}
//                       >
//                         🗑️
//                       </Button>
//                     </td>
//                   </tr>
//                 ))
//               ) : (
//                 <tr>
//                   <td colSpan={8} style={{ textAlign: "center", padding: "20px", color: "#999" }}>
//                     No metrics defined yet. Click “Generate Metrics”.
//                   </td>
//                 </tr>
//               )}
//             </tbody>
//           </table>
//         </Box>
//       </Card>

//       {/* Footer Buttons */}
//       <Box display="flex" justifyContent="flex-end" mt={4} gap={2}>
//         <Button variant="outlined">Save Draft</Button>
//         <Button
//             variant="contained"
//             color="primary"
//             onClick={() =>
//               navigate("/dataset-generation", {
//                 state: {
//                   metrics: metrics, // 👈 passing backend metrics
//                   applicationSummary,
//                   trustWeights,
//                   risks,
//                 },
//               })
//             }
//           >
//             Proceed →
//           </Button>

//         {/* <Button variant="contained" color="primary" onClick={() => navigate("/test-lab")}>
//           Proceed →
//         </Button> */}
//       </Box>
//     </Box>
//   );
// };

// export default MetricsDefinitionPage;

// // src/pages/MetricsDefinitionPage.js
// import React, { useState, useEffect } from "react";
// import {
//   Box,
//   Grid,
//   Typography,
//   Button,
//   Chip,
//   Card,
//   CardContent,
//   LinearProgress,
//   CircularProgress,
//   Alert,
//   Accordion,
//   AccordionSummary,
//   AccordionDetails,
// } from "@mui/material";
// import { ExpandMore } from "@mui/icons-material";
// import axios from "axios";
// import { useNavigate } from "react-router-dom";

// const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
// const SIDEBAR_WIDTH = 64;
// const CATEGORIES = ["Accuracy", "Fairness", "Privacy", "Ethics", "Robustness", "Security", "Reliability"];

// const MetricsDefinitionPage = () => {
//   const navigate = useNavigate();
//   const [risks, setRisks] = useState([]);
//   const [metrics, setMetrics] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [generating, setGenerating] = useState(false);
//   const [error, setError] = useState(null);
//   const [successMessage, setSuccessMessage] = useState(null);
//   const [categoryCounts, setCategoryCounts] = useState({});

//   // Fetch risks and existing metrics on component mount
//   useEffect(() => {
//     const fetchData = async () => {
//       setLoading(true);
//       try {
//         const response = await axios.get(`${baseUrl}/metrics-definition`);
//         if (response.data.success) {
//           if (response.data.previousStage && response.data.previousStage.risks) {
//             setRisks(response.data.previousStage.risks);
//           }
//           if (response.data.data && response.data.data.metrics) {
//             setMetrics(response.data.data.metrics);
//           }
//         }
//       } catch (err) {
//         console.error("Error fetching data:", err);
//         setError("Failed to load data");
//       } finally {
//         setLoading(false);
//       }
//     };
//     fetchData();
//   }, []);

//   // Update category counts when risks change
//   useEffect(() => {
//     const counts = {};
//     CATEGORIES.forEach((cat) => (counts[cat] = 0));

//     risks.forEach((risk) => {
//       if (risk.category && counts.hasOwnProperty(risk.category)) {
//         counts[risk.category]++;
//       }
//     });

//     setCategoryCounts(counts);
//   }, [risks]);

//   // Handle Generate Metrics button click
//   const handleGenerateMetrics = async () => {
//     if (!risks || risks.length === 0) {
//       setError("No risks available. Please complete Risk Identification first.");
//       return;
//     }

//     setGenerating(true);
//     setError(null);
//     setSuccessMessage(null);

//     try {
//       console.log("Generating metrics for risks:", risks);

//       const response = await axios.post(`${baseUrl}/metrics-definition`, {
//         risks: risks,
//         generateMetrics: true,
//       });

//       console.log("Backend response:", response.data);

//       if (response.data.success) {
//         if (response.data.data && response.data.data.metrics) {
//           const generatedMetrics = response.data.data.metrics;
//           console.log(`Successfully generated ${generatedMetrics.length} metrics:`, generatedMetrics);
//           setMetrics(generatedMetrics);
//           setSuccessMessage(
//             `Successfully generated ${generatedMetrics.length} metric${generatedMetrics.length > 1 ? "s" : ""}!`
//           );
//           setTimeout(() => setSuccessMessage(null), 5000);
//         } else {
//           setError("Response received but no metrics found in the data.");
//         }
//       } else {
//         setError(response.data.error || "Failed to generate metrics. Please try again.");
//       }
//     } catch (err) {
//       console.error("Error generating metrics:", err);
//       setError(err.response?.data?.error || "Failed to generate metrics. Please check the console for details.");
//     } finally {
//       setGenerating(false);
//     }
//   };

//   // Get category color
//   const getCategoryColor = (category) => {
//     const colorMap = {
//       Accuracy: "primary",
//       Fairness: "secondary",
//       Privacy: "error",
//       Ethics: "warning",
//       Robustness: "info",
//       Security: "error",
//       Reliability: "success",
//     };
//     return colorMap[category] || "default";
//   };

//   if (loading) {
//     return (
//       <Box
//         sx={{
//           display: "flex",
//           justifyContent: "center",
//           alignItems: "center",
//           height: "100vh",
//           ml: { xs: 0, md: `${SIDEBAR_WIDTH}px` },
//         }}
//       >
//         <CircularProgress />
//       </Box>
//     );
//   }

//   return (
//     <Box
//       sx={{
//         ml: { xs: 0, md: `${SIDEBAR_WIDTH}px` },
//         p: 4,
//         backgroundColor: "#f9f9f9",
//         minHeight: "100vh",
//         overflow: "auto",
//       }}
//     >
//       {/* Page Header */}
//       <Typography variant="h5" fontWeight={600} mb={1}>
//         Trust Metrics Definition
//       </Typography>
//       <Typography variant="body2" color="text.secondary" mb={4}>
//         Transform risks into measurable, quantifiable trust metrics with thresholds
//       </Typography>

//       {error && (
//         <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
//           {error}
//         </Alert>
//       )}

//       {successMessage && (
//         <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
//           {successMessage}
//         </Alert>
//       )}

//       {/* Inputs from Previous Stage - Display Risks Dynamically */}
//       <Card sx={{ borderRadius: 3, mb: 3 }}>
//         <CardContent>
//           <Typography variant="subtitle1" fontWeight={600} mb={2}>
//             Inputs from Previous Stage (Risk Identification)
//           </Typography>

//           {risks && risks.length > 0 ? (
//             <>
//               <Grid container spacing={2}>
//                 <Grid item xs={12} sm={3}>
//                   <Typography variant="body2" color="text.secondary">
//                     Total Risks Identified
//                   </Typography>
//                   <Typography variant="subtitle2" fontWeight={600}>
//                     {risks.length}
//                   </Typography>
//                 </Grid>
//                 {CATEGORIES.map((cat, idx) => {
//                   const count = categoryCounts[cat] || 0;
//                   if (count === 0) return null;
//                   return (
//                     <Grid item xs={12} sm={2.25} key={idx}>
//                       <Typography variant="body2" color="text.secondary">
//                         {cat}
//                       </Typography>
//                       <Typography variant="subtitle2" fontWeight={600}>
//                         {count}
//                       </Typography>
//                     </Grid>
//                   );
//                 })}
//               </Grid>

//               <Box mt={2} display="flex" flexWrap="wrap" gap={1}>
//                 {risks.slice(0, 10).map((risk, index) => (
//                   <Chip
//                     key={risk.id || index}
//                     label={risk.title}
//                     color={getCategoryColor(risk.category)}
//                     variant="outlined"
//                     size="small"
//                   />
//                 ))}
//                 {risks.length > 10 && (
//                   <Chip label={`+${risks.length - 10} more`} variant="outlined" size="small" />
//                 )}
//               </Box>

//               <Box mt={3}>
//                 <Button
//                   variant="contained"
//                   sx={{
//                     textTransform: "none",
//                     backgroundColor: "#007BFF",
//                     "&:hover": { backgroundColor: "#0056d2" },
//                   }}
//                   onClick={handleGenerateMetrics}
//                   disabled={generating}
//                   startIcon={generating ? <CircularProgress size={16} /> : null}
//                 >
//                   {generating ? "Generating Metrics..." : "Generate Metrics"}
//                 </Button>
//               </Box>
//             </>
//           ) : (
//             <Alert severity="warning">
//               No risks found. Please complete Risk Identification first.
//             </Alert>
//           )}
//         </CardContent>
//       </Card>

//       {/* Risk Register & Weight Distribution */}
//       <Grid container spacing={2} mb={3}>
//         <Grid item xs={12} md={6}>
//           <Card sx={{ borderRadius: 3, height: "100%" }}>
//             <CardContent>
//               <Typography variant="subtitle1" fontWeight={600} mb={2}>
//                 Risk Register
//               </Typography>
//               <Grid container spacing={2}>
//                 <Grid item xs={6} sm={2.4}>
//                   <Typography variant="body2" color="text.secondary">
//                     All Risks
//                   </Typography>
//                   <Typography variant="h6" fontWeight={600}>
//                     {risks.length}
//                   </Typography>
//                 </Grid>
//                 {CATEGORIES.map((label, i) => (
//                   <Grid item xs={6} sm={2.4} key={i}>
//                     <Typography variant="body2" color="text.secondary">
//                       {label}
//                     </Typography>
//                     <Typography variant="h6" fontWeight={600}>
//                       {categoryCounts[label] || 0}
//                     </Typography>
//                   </Grid>
//                 ))}
//               </Grid>
//               <Typography variant="body2" color="primary" mt={2} sx={{ cursor: "pointer" }}>
//                 View Metric Library
//               </Typography>
//             </CardContent>
//           </Card>
//         </Grid>

//         {/* Weight Distribution */}
//         <Grid item xs={12} md={6}>
//           <Card sx={{ borderRadius: 3, height: "100%" }}>
//             <CardContent>
//               <Typography variant="subtitle1" fontWeight={600} mb={2}>
//                 Weight Distribution
//               </Typography>
//               {CATEGORIES.filter((cat) => (categoryCounts[cat] || 0) > 0).map((label, i) => {
//                 const count = categoryCounts[label] || 0;
//                 const percentage = risks.length > 0 ? (count / risks.length) * 100 : 0;
//                 return (
//                   <Box key={i} mb={1.5}>
//                     <Typography variant="body2">{label}</Typography>
//                     <LinearProgress
//                       variant="determinate"
//                       value={percentage}
//                       sx={{
//                         height: 8,
//                         borderRadius: 5,
//                         backgroundColor: "#e0e0e0",
//                       }}
//                     />
//                   </Box>
//                 );
//               })}
//             </CardContent>
//           </Card>
//         </Grid>
//       </Grid>

//       {/* Trust Metrics Library */}
//       <Card sx={{ borderRadius: 3 }}>
//         <CardContent>
//           {metrics.length > 0 ? (
//             <>
//               <Typography variant="subtitle1" fontWeight={600} mb={2}>
//                 Generated Metrics ({metrics.length})
//               </Typography>
//               <Box sx={{ maxHeight: "500px", overflowY: "auto" }}>
//                 {metrics.map((metric, index) => (
//                   <Accordion key={metric.id || index} sx={{ mb: 1 }}>
//                     <AccordionSummary expandIcon={<ExpandMore />}>
//                       <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: 2 }}>
//                         <Typography fontWeight="bold" sx={{ flex: 1 }}>
//                           {metric.name}
//                         </Typography>
//                         <Chip
//                           label={metric.riskCategory}
//                           color={getCategoryColor(metric.riskCategory)}
//                           size="small"
//                         />
//                         <Chip label={metric.metricType} variant="outlined" size="small" />
//                         <Chip label={metric.unit} variant="outlined" size="small" />
//                       </Box>
//                     </AccordionSummary>
//                     <AccordionDetails>
//                       <Typography variant="body2" mb={2}>
//                         <strong>Description:</strong> {metric.description}
//                       </Typography>
//                       {metric.measurementMethod && (
//                         <Typography variant="body2" mb={2}>
//                           <strong>Measurement Method:</strong> {metric.measurementMethod}
//                         </Typography>
//                       )}
//                       {metric.threshold && (
//                         <Box mb={2}>
//                           <Typography variant="body2" fontWeight="bold" mb={1}>
//                             Thresholds:
//                           </Typography>
//                           <Grid container spacing={2}>
//                             <Grid item xs={3}>
//                               <Typography variant="caption" color="text.secondary">
//                                 Min: {metric.threshold.min}
//                               </Typography>
//                             </Grid>
//                             <Grid item xs={3}>
//                               <Typography variant="caption" color="text.secondary">
//                                 Target: {metric.threshold.target}
//                               </Typography>
//                             </Grid>
//                             <Grid item xs={3}>
//                               <Typography variant="caption" color="text.secondary">
//                                 Critical: {metric.threshold.critical}
//                               </Typography>
//                             </Grid>
//                             <Grid item xs={3}>
//                               <Typography variant="caption" color="text.secondary">
//                                 Max: {metric.threshold.max}
//                               </Typography>
//                             </Grid>
//                           </Grid>
//                         </Box>
//                       )}
//                       {metric.frequency && (
//                         <Typography variant="body2">
//                           <strong>Frequency:</strong> {metric.frequency}
//                         </Typography>
//                       )}
//                     </AccordionDetails>
//                   </Accordion>
//                 ))}
//               </Box>
//             </>
//           ) : (
//             <Box sx={{ textAlign: "center", py: 6 }}>
//               <Typography variant="subtitle1" fontWeight={600}>
//                 No metrics defined yet
//               </Typography>
//               <Typography variant="body2" color="text.secondary" mb={2}>
//                 Click "Generate Metrics" to generate metrics using AI based on identified risks
//               </Typography>
//               <Button
//                 variant="outlined"
//                 onClick={handleGenerateMetrics}
//                 disabled={generating || risks.length === 0}
//               >
//                 {generating ? "Generating..." : "Generate Metrics"}
//               </Button>
//             </Box>
//           )}
//         </CardContent>
//       </Card>

//       {/* Footer buttons */}
//       <Box mt={3} display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
//         <Button variant="outlined" sx={{ textTransform: "none" }}>
//           Save Draft
//         </Button>
//         <Button
//           variant="contained"
//           disabled={metrics.length === 0}
//           sx={{
//             textTransform: "none",
//             backgroundColor: "#007BFF",
//             "&:hover": { backgroundColor: "#0056d2" },
//           }}
//           onClick={() => navigate("/dataset-generation")}
//         >
//           Dataset Generation →
//         </Button>
//       </Box>
//     </Box>
//   );
// };

// export default MetricsDefinitionPage;
